import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as R from 'ramda'
import { EventTypeName } from '@pbt/pbt-ui-components'

import {
  APPOINTMENT_TYPES_WITH_NO_SHOW_CANCELLATION_POLICY,
  type AppointmentTypeName,
  isExcludedNoShowConsentBusinessAppointmentType,
} from '~/constants/appointmentTypes'
import { fetchAppointmentTypesList } from '~/store/actions/appointmentTypes'
import { useIsNoShowCancellationPenaltyRefundEnabled } from '~/store/hooks/business'
import { getAppointmentTypesMap } from '~/store/reducers/appointmentTypes'
import { getCurrentBusinessId } from '~/store/reducers/auth'
import type { BusinessAppointmentType } from '~/types/entities/businessAppointmentType'
import { isNilOrEmpty } from '~/utils'
import { useEventType } from '~/utils/useEventType'

export const useGetIsNoShowConsentAppointmentType = () => {
  const dispatch = useDispatch()
  const businessId = useSelector(getCurrentBusinessId)
  const businessAppointmentTypesMap = useSelector(getAppointmentTypesMap)
  const isNoShowPenaltyRefundEnabled =
    useIsNoShowCancellationPenaltyRefundEnabled()

  const AppointmentEventSubTypes = useEventType(
    EventTypeName.Appointment,
    'subTypes',
  ).filter((type: { name: AppointmentTypeName }) =>
    APPOINTMENT_TYPES_WITH_NO_SHOW_CANCELLATION_POLICY.includes(type.name),
  )

  const excludedBusinessAppointmentTypes = R.pipe(
    R.pickBy((businessAppointmentType: BusinessAppointmentType) =>
      isExcludedNoShowConsentBusinessAppointmentType(
        businessAppointmentType?.name,
      ),
    ),
    R.keys,
  )(businessAppointmentTypesMap)

  const isNoShowConsentAppointmentType = (
    appointmentTypeId: string | undefined,
  ) =>
    Boolean(appointmentTypeId) &&
    AppointmentEventSubTypes.some(
      (type: { id: string }) => type.id === appointmentTypeId,
    )

  const isExcludedBusinessAppointmentType = (
    businessAppointmentTypeId: string | undefined,
  ) =>
    isNoShowPenaltyRefundEnabled &&
    businessAppointmentTypeId &&
    excludedBusinessAppointmentTypes.includes(businessAppointmentTypeId)

  useEffect(() => {
    if (isNilOrEmpty(businessAppointmentTypesMap) && businessId) {
      dispatch(
        fetchAppointmentTypesList({
          businessId,
          searchFilter: {
            from: 0,
            to: 200,
          },
        }),
      )
    }
  }, [businessAppointmentTypesMap, businessId])

  return ({
    appointmentTypeId,
    businessAppointmentTypeId,
  }: {
    appointmentTypeId: string | undefined
    businessAppointmentTypeId: string | undefined
  }) =>
    isNoShowConsentAppointmentType(appointmentTypeId) &&
    !isExcludedBusinessAppointmentType(businessAppointmentTypeId)
}
